import React, { useState } from 'react';
import { PhoneIcon, MailIcon, CheckCircleIcon } from 'lucide-react';
type FormState = {
  name: string;
  email: string;
  phone: string;
  service: string;
  location: string;
};
type FormErrors = Partial<Record<keyof FormState, string>>;
export function BookingSection() {
  const [form, setForm] = useState<FormState>({
    name: '',
    email: '',
    phone: '',
    service: '',
    location: ''
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const validate = (): FormErrors => {
    const errs: FormErrors = {};
    if (!form.name.trim()) errs.name = 'Name is required';
    if (!form.email.trim()) errs.email = 'Email is required';else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = 'Enter a valid email';
    if (!form.phone.trim()) errs.phone = 'Phone number is required';
    if (!form.service) errs.service = 'Please select a service';
    if (!form.location) errs.location = 'Please select a location';
    return errs;
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    setSubmitting(true);
    setTimeout(() => {
      setSubmitting(false);
      setSubmitted(true);
    }, 1200);
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name as keyof FormState]) setErrors(prev => ({
      ...prev,
      [name]: undefined
    }));
  };
  const inputClass = (hasError: boolean) => `w-full px-4 py-3 border rounded text-black placeholder-gray-400 focus:outline-none focus:ring-2 focus:border-transparent text-sm transition-colors ${hasError ? 'border-red-400 bg-red-50' : 'border-gray-200'}`;
  return <section id="booking" className="w-full" data-id="element-39">
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2" data-id="element-40">
        {/* Left: Info Panel */}
        <div className="px-8 sm:px-12 py-16 lg:py-20 flex flex-col justify-center" style={{
        background: '#1a1a1a'
      }} data-id="element-41">
          <p className="font-bold text-sm uppercase tracking-widest mb-4" style={{
          color: '#CC0000'
        }} data-id="element-42">
            Book an Appointment
          </p>
          <h2 className="text-3xl sm:text-4xl font-black text-white mb-4 leading-tight" data-id="element-43">
            Schedule Your Service
          </h2>
          <p className="text-base leading-relaxed mb-10" style={{
          color: 'rgba(255,255,255,0.80)'
        }} data-id="element-44">
            Fill out the form and we'll confirm your appointment within the
            hour. Walk-ins also welcome at all 3 locations.
          </p>

          <div className="space-y-5" data-id="element-45">
            <a href="tel:3012549001" className="flex items-center gap-4 group" data-id="element-46">
              <div className="w-11 h-11 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0 group-hover:bg-white/30 transition-colors" data-id="element-47">
                <PhoneIcon size={18} className="text-white" data-id="element-48" />
              </div>
              <div data-id="element-49">
                <div className="text-xs font-semibold uppercase tracking-wide" style={{
                color: '#AA9C8F'
              }} data-id="element-50">
                  Phone
                </div>
                <div className="text-white font-bold text-base" data-id="element-51">
                  (301) 254-9001
                </div>
              </div>
            </a>

            <a href="mailto:info@ameritechtires.com" className="flex items-center gap-4 group" data-id="element-52">
              <div className="w-11 h-11 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0 group-hover:bg-white/30 transition-colors" data-id="element-53">
                <MailIcon size={18} className="text-white" data-id="element-54" />
              </div>
              <div data-id="element-55">
                <div className="text-xs font-semibold uppercase tracking-wide" style={{
                color: '#AA9C8F'
              }} data-id="element-56">
                  Email
                </div>
                <div className="text-white font-bold text-base" data-id="element-57">
                  info@ameritechtires.com
                </div>
              </div>
            </a>
          </div>

          <div className="mt-12 pt-8 border-t border-white/20" data-id="element-58">
            <div className="flex items-center gap-2 text-sm" style={{
            color: 'rgba(255,255,255,0.80)'
          }} data-id="element-59">
              <CheckCircleIcon size={16} className="text-white" data-id="element-60" />
              Confirmation within 1 hour
            </div>
            <div className="flex items-center gap-2 text-sm mt-2" style={{
            color: 'rgba(255,255,255,0.80)'
          }} data-id="element-61">
              <CheckCircleIcon size={16} className="text-white" data-id="element-62" />
              No credit card required to book
            </div>
          </div>
        </div>

        {/* Right: Form */}
        <div className="bg-white px-8 sm:px-12 py-16 lg:py-20" data-id="element-63">
          {submitted ? <div className="flex flex-col items-center justify-center h-full text-center py-12" data-id="element-64">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-5" data-id="element-65">
                <CheckCircleIcon size={32} className="text-green-500" data-id="element-66" />
              </div>
              <h3 className="text-2xl font-black text-black mb-3" data-id="element-67">
                Appointment Requested!
              </h3>
              <p className="text-gray-500 text-base max-w-sm" data-id="element-68">
                We'll confirm your appointment within the hour. Check your email
                for details.
              </p>
              <button onClick={() => {
            setSubmitted(false);
            setForm({
              name: '',
              email: '',
              phone: '',
              service: '',
              location: ''
            });
          }} className="mt-8 font-bold text-sm transition-colors" style={{
            color: '#822433'
          }} onMouseEnter={e => e.currentTarget.style.color = '#6B1D29'} onMouseLeave={e => e.currentTarget.style.color = '#822433'} data-id="element-69">
                Book Another Appointment
              </button>
            </div> : <form onSubmit={handleSubmit} noValidate className="space-y-5" data-id="element-70">
              <div data-id="element-71">
                <label htmlFor="name" className="block text-sm font-bold text-black mb-1.5" data-id="element-72">
                  Full Name{' '}
                  <span style={{
                color: '#CC0000'
              }} data-id="element-73">
                    *
                  </span>
                </label>
                <input id="name" name="name" type="text" value={form.name} onChange={handleChange} placeholder="John Smith" className={inputClass(!!errors.name)} style={{
              '--tw-ring-color': '#822433'
            } as React.CSSProperties} aria-describedby={errors.name ? 'name-error' : undefined} data-id="element-74" />
                {errors.name && <p id="name-error" className="mt-1 text-xs text-red-500" data-id="element-75">
                    {errors.name}
                  </p>}
              </div>

              <div data-id="element-76">
                <label htmlFor="email" className="block text-sm font-bold text-black mb-1.5" data-id="element-77">
                  Email Address{' '}
                  <span style={{
                color: '#CC0000'
              }} data-id="element-78">
                    *
                  </span>
                </label>
                <input id="email" name="email" type="email" value={form.email} onChange={handleChange} placeholder="john@example.com" className={inputClass(!!errors.email)} aria-describedby={errors.email ? 'email-error' : undefined} data-id="element-79" />
                {errors.email && <p id="email-error" className="mt-1 text-xs text-red-500" data-id="element-80">
                    {errors.email}
                  </p>}
              </div>

              <div data-id="element-81">
                <label htmlFor="phone" className="block text-sm font-bold text-black mb-1.5" data-id="element-82">
                  Phone Number{' '}
                  <span style={{
                color: '#CC0000'
              }} data-id="element-83">
                    *
                  </span>
                </label>
                <input id="phone" name="phone" type="tel" value={form.phone} onChange={handleChange} placeholder="(301) 555-0000" className={inputClass(!!errors.phone)} aria-describedby={errors.phone ? 'phone-error' : undefined} data-id="element-84" />
                {errors.phone && <p id="phone-error" className="mt-1 text-xs text-red-500" data-id="element-85">
                    {errors.phone}
                  </p>}
              </div>

              <div data-id="element-86">
                <label htmlFor="service" className="block text-sm font-bold text-black mb-1.5" data-id="element-87">
                  Service Needed{' '}
                  <span style={{
                color: '#CC0000'
              }} data-id="element-88">
                    *
                  </span>
                </label>
                <select id="service" name="service" value={form.service} onChange={handleChange} className={`${inputClass(!!errors.service)} appearance-none bg-white ${!form.service ? 'text-gray-400' : ''}`} aria-describedby={errors.service ? 'service-error' : undefined} data-id="element-89">
                  <option value="" disabled data-id="element-90">
                    Select a service...
                  </option>
                  <option value="new-tires" data-id="element-91">New Tires</option>
                  <option value="installation" data-id="element-92">Tire Installation</option>
                  <option value="rotation" data-id="element-93">Tire Rotation</option>
                  <option value="flat-repair" data-id="element-94">Flat Repair</option>
                  <option value="inspection" data-id="element-95">Free Inspection</option>
                  <option value="other" data-id="element-96">Other</option>
                </select>
                {errors.service && <p id="service-error" className="mt-1 text-xs text-red-500" data-id="element-97">
                    {errors.service}
                  </p>}
              </div>

              <div data-id="element-98">
                <label htmlFor="location" className="block text-sm font-bold text-black mb-1.5" data-id="element-99">
                  Preferred Location{' '}
                  <span style={{
                color: '#CC0000'
              }} data-id="element-100">
                    *
                  </span>
                </label>
                <select id="location" name="location" value={form.location} onChange={handleChange} className={`${inputClass(!!errors.location)} appearance-none bg-white ${!form.location ? 'text-gray-400' : ''}`} aria-describedby={errors.location ? 'location-error' : undefined} data-id="element-101">
                  <option value="" disabled data-id="element-102">
                    Select a location...
                  </option>
                  <option value="cottage-city" data-id="element-103">Cottage City</option>
                  <option value="capitol-heights" data-id="element-104">Capitol Heights</option>
                  <option value="suitland" data-id="element-105">Suitland</option>
                </select>
                {errors.location && <p id="location-error" className="mt-1 text-xs text-red-500" data-id="element-106">
                    {errors.location}
                  </p>}
              </div>

              <button type="submit" disabled={submitting} className="w-full text-white font-black text-base py-4 rounded transition-colors duration-200 mt-2 disabled:opacity-50" style={{
            background: submitting ? '#AA9C8F' : '#CC0000'
          }} onMouseEnter={e => {
            if (!submitting) e.currentTarget.style.background = '#AA0000';
          }} onMouseLeave={e => {
            if (!submitting) e.currentTarget.style.background = '#CC0000';
          }} data-id="element-107">
                {submitting ? 'Booking...' : 'Book Now'}
              </button>
            </form>}
        </div>
      </div>
    </section>;
}